export interface DiffRow {
  key: string;
  before: string;
  after: string;
  changed: boolean;
}

const EMPTY_CLIENT = '—';

const show = (v: unknown): string => (v === undefined ? '' : JSON.stringify(v));

/** Flattens metadata.before/after into table rows; `changed` mirrors changed_fields or presence on one side only. */
export function buildDiffRows(meta: Record<string, unknown> | null): DiffRow[] {
  const before = (meta?.['before'] ?? null) as Record<string, unknown> | null;
  const after = (meta?.['after'] ?? null) as Record<string, unknown> | null;
  if (!before && !after) return [];
  const changed = new Set((meta?.['changed_fields'] as string[] | undefined) ?? []);
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  // A create (after only) or delete (before only) counts every field as changed.
  const oneSided = !before || !after;
  return keys.map((key) => ({
    key,
    before: show(before?.[key]),
    after: show(after?.[key]),
    changed: oneSided || changed.has(key),
  }));
}

const BROWSERS: [RegExp, string][] = [
  [/Edg\//i, 'Edge'],
  [/Chrome\//i, 'Chrome'],
  [/Firefox\//i, 'Firefox'],
  [/Safari\//i, 'Safari'],
];

const PLATFORMS: [RegExp, string][] = [
  [/Windows/i, 'Windows'],
  [/Mac OS X|Macintosh/i, 'macOS'],
  [/Android/i, 'Android'],
  [/iPhone|iPad/i, 'iOS'],
  [/Linux/i, 'Linux'],
];

const firstMatch = (s: string, table: [RegExp, string][]): string => table.find(([re]) => re.test(s))?.[1] ?? 'Other';

/** Coarse UA sniffing for the Client column; order matters (Edge/Chrome embed "Safari"). */
export function parseUserAgent(ua: string | null): {
  browser: string;
  platform: string;
} {
  const s = (ua ?? '').trim();
  if (!s) return { browser: EMPTY_CLIENT, platform: EMPTY_CLIENT };
  return {
    browser: firstMatch(s, BROWSERS),
    platform: firstMatch(s, PLATFORMS),
  };
}

/**
 * `<input type="date">` yields YYYY-MM-DD. The backend does `new Date()` on both bounds, and
 * `new Date('YYYY-MM-DD')` parses as UTC midnight while `'YYYY-MM-DDT23:59:59.999'` (no Z) parses as
 * local time, so a bare start date and an expanded end date would sit in different time zones.
 * Both helpers emit the T-form so the two bounds are interpreted the same way (server-local).
 */
export function startOfDay(dateOnly: string): string {
  return `${dateOnly}T00:00:00.000`;
}

export function endOfDay(dateOnly: string): string {
  return `${dateOnly}T23:59:59.999`;
}
